//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode } from 'react';
import { FormControl, InputLabel, Select, FormHelperText, SxProps, FormControlProps } from '@mui/material';
import styled from '@emotion/styled';
import PolarisIcon from '../PolarisIcon';

type DropdownSelectProps = {
  label: string;
  value: any;
  children: ReactNode;
  name?: string;
  helperText?: string;
  fullWidth?: FormControlProps['required'];
  onChange?: (value: any, name?: string) => void;
  displayEmpty?: boolean;
  handleChange?: React.ChangeEventHandler<HTMLSelectElement>;
  sx?: SxProps;
  disabled?: FormControlProps['disabled'];
  error?: FormControlProps['error'];
  id?: string;
  required?: FormControlProps['required'];
  size?: FormControlProps['size'];
  loading?: boolean;
};

const DropdownSelect = ({
  label,
  name,
  value,
  children,
  onChange,
  helperText,
  displayEmpty,
  handleChange,
  sx,
  fullWidth,
  disabled,
  error,
  id,
  required,
  size,
  loading
}: DropdownSelectProps) => {
  const onValueChange = (e: any) => {
    if (onChange) {
      onChange(e.target.value, name);
    }
    // we're using formik in some places, so we need to pass the event to the handleChange function
    if (handleChange) {
      handleChange(e);
    }
  };

  return (
    <Dropdown variant="filled" sx={sx} fullWidth={fullWidth} disabled={disabled || loading} error={error} id={id} required={required} size={size}>
      {loading ? (
        <InputLabel>
          <LoadingIcon icon="loading_dots" height={39} />
        </InputLabel>
      ) : (
        label && <InputLabel id={name}>{label}</InputLabel>
      )}
      <Select
        labelId={name}
        id={name}
        name={name}
        value={value || ''}
        onChange={onValueChange}
        displayEmpty={displayEmpty}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' }
        }}
      >
        {children}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Dropdown>
  );
};

export default DropdownSelect;

const Dropdown = styled(FormControl)`
  min-width: 115px;
`;

const LoadingIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.primary.main};
  margin-top: ${({ theme }) => theme.spacing(-1)};
`;
